import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { cleanupDuplicateNodes, type DuplicateCleanupConfig } from 'webapp/utils/html';

import { containerEventName } from 'webapp/modifiers/visible-check-container';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type {
	AddTagAction,
	DeleteAction,
	ToggleFavoriteAction,
} from 'webapp/models-custom/batch-select';
import type Goal from 'webapp/models-custom/goal';

export interface GoalsBaseArgs {
	model: Goal[];
	openView: (viewId: string) => void;
}

type Action = ToggleFavoriteAction | AddTagAction | DeleteAction;

export default abstract class GoalsBaseComponent<
	T extends GoalsBaseArgs = GoalsBaseArgs,
> extends Component<T> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;

	abstract duplicateCleanupConfig: DuplicateCleanupConfig;
	abstract batchSelect: BatchSelect<Goal>;

	visibleCheckContainerNodes: HTMLElement[] = [];

	addButtonText = 'New goal';

	get model() {
		return this.args.model;
	}

	get isDemoAccount(): boolean {
		return this.accountService.isDemoAccount;
	}

	get areAllSelectedItemsFavorited(): boolean {
		return this.selectedItems.every((item) => item.favorite);
	}

	@action
	doBatchAction(actionName: DeleteAction) {
		return this[actionName]();
	}

	@action
	batchDelete() {
		return this.mmGoalsService.batchDelete(this.selectedItems);
	}

	@action
	batchToggleFavorite() {
		if (this.areAllSelectedItemsFavorited) {
			return this.mmGoalsService.batchUnfavorite(this.selectedItems);
		} else {
			return this.mmGoalsService.batchFavorite(this.selectedItems);
		}
	}

	@action
	batchUpdateTags() {
		return this.mmGoalsService.batchUpdateTags(this.selectedItems);
	}

	get addButtonContainerNode() {
		return !this.selectedItems.length ? this.navigationService.addButtonContainerNode : null;
	}

	get batchButtonContainerNode() {
		return this.selectedItems.length ? this.navigationService.batchButtonContainerNode : null;
	}

	get selectedItems(): Goal[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<Action>[] {
		const batchSelect = this.batchSelect;

		return [
			batchSelect.toggleFavoriteOption,
			batchSelect.addTagsOption,
			batchSelect.deleteOption,
		];
	}

	@action
	favoriteGoalsFilterFn(items: Goal[]) {
		return items.filter((item: Goal) => item.favorite);
	}

	@action
	nonFavoriteGoalsFilterFn(items: Goal[]) {
		return items.filter((item: Goal) => !item.favorite);
	}

	@action
	cleanupDuplicateNodes(): void {
		cleanupDuplicateNodes(this.duplicateCleanupConfig);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupFavoritesView(node: HTMLElement): void {
		this.setupView(node, true);
	}

	@action
	setupNonFavoritesView(node: HTMLElement): void {
		this.setupView(node, false);
	}

	@action
	setupView(node: HTMLElement, _sortingFavorites?: boolean): void {
		this.addVisibleCheckContainerNode(node);
	}

	@action
	protected doVisibleCheck() {
		this.visibleCheckContainerNodes.forEach((node) => {
			this.triggerContentChange(node);
		});
	}

	protected addVisibleCheckContainerNode(node: HTMLElement) {
		this.visibleCheckContainerNodes.push(node);
	}
}
